<template>
    <div class="h-full min-w-0 overflow-hidden">
        <div class="bg-white dark:bg-gray-800 h-full rounded-lg overflow-hidden min-w-0">
            <!-- List header -->
            <div class="p-4 border-b border-gray-200 dark:border-gray-700">
                <div class="flex items-center justify-between">
                    <div class="text-lg font-semibold text-gray-900 dark:text-white">
                        <span v-if="showSpecificDate">
                            {{ $t('schedule.eventsFor') }} {{ formatSpecificDate(currentDate) }}
                        </span>
                        <span v-else>
                            {{ $t('schedule.eventsFor') }} {{ formatMonthYear(currentDate) }}
                        </span>
                    </div>
                    <button 
                        v-if="showSpecificDate"
                        @click="$emit('back-to-calendar')"
                        class="text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium px-3 py-1 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                    >
                        {{ $t('schedule.backToCalendar') }}
                    </button>
                </div>
            </div>
            
            <!-- Events list -->
            <div class="divide-y divide-gray-200 dark:divide-gray-700 overflow-y-auto h-[calc(100%-80px)] min-w-0">
                <div 
                    v-for="event in getEventsForMonth(currentDate)" 
                    :key="event.id"
                    @click="selectEvent(event)"
                    class="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors min-w-0 overflow-hidden"
                >
                    <div class="flex items-center justify-between min-w-0">
                        <div class="flex items-center space-x-3 min-w-0 flex-1">
                            <div 
                                class="w-3 h-3 rounded-full flex-shrink-0"
                                :class="getEventColor(event)"
                                :style="getEventStyles(event)"
                            ></div>
                            <div class="min-w-0 flex-1">
                                <div class="font-medium text-gray-900 dark:text-white truncate">
                                    {{ event.title }}
                                </div>
                                <div class="text-sm text-gray-500 dark:text-gray-400 truncate">
                                    {{ formatEventTime(event) }}
                                </div>
                            </div>
                        </div>
                        <div class="text-sm text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                            {{ formatEventDate(event) }}
                        </div>
                    </div>
                </div>
                
                <!-- Empty state -->
                <div v-if="getEventsForMonth(currentDate).length === 0" class="p-8 text-center">
                    <div class="text-gray-500 dark:text-gray-400">
                        {{ $t('schedule.noEventsScheduled') }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue'
import { isTailwindColor } from '@/utilities/color'
import { useI18n } from 'vue-i18n'

// Props
const props = defineProps({
    currentDate: {
        type: Date,
        default: () => new Date()
    },
    events: {
        type: Array,
        default: () => []
    },
    categories: {
        type: Array,
        default: () => []
    },
    showSpecificDate: {
        type: Boolean,
        default: false
    }
})

// Emits
const emit = defineEmits(['event-select', 'back-to-calendar'])

const { locale } = useI18n()

// Methods
const getEventsForMonth = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    return props.events.filter(event => {
        const eventDate = new Date(event.startTime)
        return eventDate.getFullYear() === year && eventDate.getMonth() === month
    }).sort((a, b) => new Date(a.date) - new Date(b.date))
}

const getEventStyles = (event) => {
    const category = props.categories.find(c => c.name === event.category)
    const color = category ? category.color : 'gray'
    
    if (isTailwindColor(color)) {
        return {}
    }
    return {
        backgroundColor: color
    }
}

const getEventColor = (event) => {
    const category = props.categories.find(c => c.name === event.category)
    const color = category ? category.color : 'gray'
    
    if (isTailwindColor(color)) {
        return `bg-${color}-500 hover:bg-${color}-600`
    }
    return ''
}

const formatMonthYear = (date) => {
    return date.toLocaleDateString(locale.value, { 
        month: 'long', 
        year: 'numeric' 
    })
}

const formatSpecificDate = (date) => {
    return date.toLocaleDateString(locale.value, { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    })
}

const formatEventTime = (event) => {
    if (!event.startTime) return ''
    return event.startTime.toLocaleTimeString(locale.value, { 
        hour: 'numeric', 
        minute: '2-digit' 
    })
}

const formatEventDate = (event) => {
    return new Date(event.startTime).toLocaleDateString(locale.value, { 
        month: 'short', 
        day: 'numeric' 
    })
}

const selectEvent = (event) => {
    emit('event-select', event)
}
</script>
